import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAdaugaTargetareTinte } from "@/hooks/api/mutate/useAdaugaTargetareTinte";
import { useToast } from "@/hooks/use-toast";
import type { TargetareCampanie } from "@/types/targetare";
import { formatNumar } from "@/utils/targetareFormatting";
import { cuiuriDinText } from "@/utils/targetareTinte";

type AdaugaTinteDialogProps = {
  deschis: boolean;
  onClose: () => void;
  cuiInitiale?: string[];
  campanii: TargetareCampanie[];
  sursa?: string;
};

const FARA_CAMPANIE = "fara";

export function AdaugaTinteDialog({
  deschis,
  onClose,
  cuiInitiale = [],
  campanii,
  sursa = "manual",
}: AdaugaTinteDialogProps) {
  const { toast } = useToast();
  const adauga = useAdaugaTargetareTinte();
  const [text, setText] = useState("");
  const [campanie, setCampanie] = useState(FARA_CAMPANIE);
  const [sector, setSector] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (deschis) setText(cuiInitiale.join("\n"));
  }, [deschis, cuiInitiale]);

  const cuiuri = cuiuriDinText(text);

  const trimite = () => {
    adauga.mutate(
      {
        cui: cuiuri,
        campanie_id: campanie === FARA_CAMPANIE ? undefined : Number(campanie),
        sector: sector.trim() || undefined,
        note: note.trim() || undefined,
        sursa,
      },
      {
        onSuccess: (r) => {
          toast({
            title: "Ținte adăugate",
            description: `${formatNumar(r.data.adaugate)} firme noi în evidență, ${formatNumar(r.data.existente)} erau deja.`,
          });
          setNote("");
          setSector("");
          onClose();
        },
        onError: (e) =>
          toast({
            title: "Adăugarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={deschis} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adaugă firme în evidența de targetare</DialogTitle>
          <DialogDescription>
            Lipește CUI-uri (unul pe rând sau separate prin virgulă) sau
            folosește selecția din raport. Firmele intră cu starea „De
            contactat”.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder="160796&#10;13267213&#10;…"
            className="font-mono text-xs"
          />
          <p className="text-xs text-muted-foreground">
            {formatNumar(cuiuri.length)} CUI-uri recunoscute
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select value={campanie} onValueChange={setCampanie}>
              <SelectTrigger>
                <SelectValue placeholder="Campanie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FARA_CAMPANIE}>Fără campanie</SelectItem>
                {campanii.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.nume}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="Sector (implicit: primul sector al firmei)"
            />
          </div>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notă (opțional)"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Renunță
          </Button>
          <Button
            onClick={trimite}
            disabled={cuiuri.length === 0 || adauga.isPending}
          >
            {adauga.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Adaugă {formatNumar(cuiuri.length)} firme
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
